import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import fetchWithAuth from "../../lib/fetchWithAuth";
import { API_BASE_URL } from "../../config";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

interface Question {
  id: number;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_option: string;
}

const ViewEditQuestions: React.FC = () => {
  const { id } = useParams(); // contest ID
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);

  useEffect(() => {
    const handleBackButton = (event: PopStateEvent) => {
      event.preventDefault();
      navigate("/admin/dashboard", { replace: true });
    };

    window.addEventListener("popstate", handleBackButton);

    return () => {
      window.removeEventListener("popstate", handleBackButton);
    };
  }, [navigate]);

  useEffect(() => {

    const loadQuestions = async () => {
      try {
        const res = await fetchWithAuth(`${API_BASE_URL}/admin/questions`);
        if (!res.ok) throw new Error("Failed to fetch questions");
        const data = await res.json();

        const filtered = data.questions.filter(
          (q: any) => q.contest_id === parseInt(id as string)
        );

        setQuestions(filtered);
      } catch (err) {
        console.error("Error fetching questions:", err);
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, [id]);

  const handleDelete = async (questionId: number) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: "This question will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      customClass: {
        popup: "rounded-xl shadow-lg",
        confirmButton: "bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700",
        cancelButton: "bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400",
      },
    });

    if (result.isConfirmed) {
      try {
        const res = await fetchWithAuth(`${API_BASE_URL}/admin/questions/${questionId}`, {
          method: "DELETE",
        });
        if (!res.ok) throw new Error("Failed to delete");

        setQuestions((prev) => prev.filter((q) => q.id !== questionId));

        MySwal.fire({
          title: "Deleted!",
          text: "The question has been deleted.",
          icon: "success",
          confirmButtonText: "OK",
          customClass: {
            popup: "rounded-xl shadow-md",
            confirmButton: "bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700",
          },
        });
      } catch (err) {
        MySwal.fire({
          title: "Error",
          text: "Failed to delete question",
          icon: "error",
          confirmButtonText: "OK",
          customClass: {
            popup: "rounded-xl shadow",
            confirmButton: "bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800",
          },
        });

        console.error("Delete error:", err);
      }
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto min-h-screen">
      {/* Back Button */}


      <button
        onClick={() => navigate("/admin/questions")}
        className="mt-12 block items-center text-indigo-600 hover:underline text-sm px-2 py-1"
      >
        ← Back to Manage Questions
      </button>



      {loading ? (
        <p className="text-center text-gray-600">Loading...</p>
      ) : questions.length === 0 ? (
        <div className="flex flex-col items-center justify-center mt-12 text-center text-gray-500">
          {!imageError ? (
            <img
              src="https://www.svgrepo.com/show/384463/empty-box.svg"
              alt="No Data"
              className="w-40 h-40 opacity-60 mb-4"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="text-6xl mb-4">📭</div>
          )}
          <p className="text-lg font-medium">No questions found for this contest.</p>
          <p className="text-sm text-gray-400 mt-1">
            You can create questions from the <strong>Manage Questions</strong> panel.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {questions.map((q, index) => (
            <div key={q.id} className="border p-5 rounded-xl shadow bg-white">
              <p className="font-semibold mb-2 text-gray-800">
                {index + 1}. {q.question_text}
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>A. {q.option_a}</li>
                <li>B. {q.option_b}</li>
                <li>C. {q.option_c}</li>
                <li>D. {q.option_d}</li>
              </ul>
              <p className="mt-3 font-medium text-green-700">
                ✅ Correct Option: {q.correct_option.toUpperCase()}
              </p>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => navigate(`/admin/edit-question/${q.id}`)}
                  className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 text-sm"
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => handleDelete(q.id)}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm"
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ViewEditQuestions;
